import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
  type ValueGetterParams,
} from 'ag-grid-community';
import { Search, Plus, FileText, X } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { extractEnvelope } from '../../lib/extractEnvelope';
import type { PostgrestError } from '@supabase/supabase-js';
import { escapeOrIlikeTerm } from '../../lib/postgrestEscape';
import { useApp } from '../../hooks/useApp';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import ArbiSelect from '../../components/ui/ArbiSelect';
import ExportButton from '../../components/ui/ExportButton';
import type { Distributor } from '../../types';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Local types ─────────────────────────────────────────────────────────────

type InvoiceStatus = 'pending' | 'matched' | 'discrepancy' | 'variance_flagged' | 'paid';
type InvoiceStatusFilter = 'all' | InvoiceStatus;

interface InvoiceRow {
  id: string;
  invoice_number: string | null;
  distributor_id: string | null;
  po_id: string | null;
  amount: number | null;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  notes: string | null;
  updated_at: string | null;
  distributors?: { name: string | null } | null;
  purchase_orders?: { po_number: string | null } | null;
}

interface AddInvoiceForm {
  invoice_number: string;
  distributor_id: string;
  po_id: string;
  amount: string;
  status: InvoiceStatus;
  due_date: string;
  notes: string;
}

interface PoOption {
  value: string;
  label: string;
}

// ── Formatting ──────────────────────────────────────────────────────────────

function fmtCurrency(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '\u2014';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-warning/15 text-warning',
  matched: 'bg-success/15 text-success',
  discrepancy: 'bg-danger/15 text-danger',
  variance_flagged: 'bg-danger/15 text-danger',
  paid: 'bg-surface-3 text-ink-muted',
};

const STATUS_TABS: ReadonlyArray<{ key: InvoiceStatusFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'matched', label: 'Matched' },
  { key: 'discrepancy', label: 'Discrepancy' },
  { key: 'variance_flagged', label: 'Variance' },
  { key: 'paid', label: 'Paid' },
] as const;

// ── Add Invoice Modal ───────────────────────────────────────────────────────

interface AddInvoiceModalProps {
  distributors: Distributor[];
  onClose: () => void;
  onSaved: () => void;
}

function AddInvoiceModal({ distributors, onClose, onSaved }: AddInvoiceModalProps): JSX.Element {
  const [form, setForm] = useState<AddInvoiceForm>({
    invoice_number: '',
    distributor_id: '',
    po_id: '',
    amount: '',
    status: 'pending',
    due_date: '',
    notes: '',
  });
  const [poOptions, setPoOptions] = useState<PoOption[]>([]);
  const [saving, setSaving] = useState<boolean>(false);

  // Load PO options when distributor changes
  useEffect(() => {
    if (!form.distributor_id) {
      setPoOptions([]);
      return;
    }
    supabase
      .from('purchase_orders')
      .select('id, po_number')
      .eq('distributor_id', form.distributor_id)
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        const rows = (data ?? []) as Array<{ id: string; po_number: string | null }>;
        setPoOptions(rows.map((p) => ({ value: p.id, label: p.po_number ?? '' })));
      });
  }, [form.distributor_id]);

  const handleSave = async (): Promise<void> => {
    if (!form.invoice_number.trim()) return;
    setSaving(true);
    const payload = {
      invoice_number: form.invoice_number.trim(),
      distributor_id: form.distributor_id || null,
      po_id: form.po_id || null,
      amount: form.amount ? parseFloat(form.amount) : null,
      status: form.status,
      due_date: form.due_date || null,
      notes: form.notes.trim() || null,
    };
    const { error } = await supabase.from('invoices').insert(payload as never);
    setSaving(false);
    if (!error) {
      onSaved();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 'var(--z-modal, 200)' }}
    >
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-surface-0/80 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add Invoice"
        className="relative bg-surface-1 border border-border rounded-xl shadow-xl w-full max-w-md mx-4"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-ink-bright">Add Invoice</h2>
          <button
            onClick={onClose}
            className="arbi-btn-ghost p-1 rounded cursor-pointer"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label
              htmlFor="inv-number"
              className="text-xs font-medium text-ink-secondary block mb-1"
            >
              Invoice Number <span className="text-danger">*</span>
            </label>
            <input
              id="inv-number"
              value={form.invoice_number}
              onChange={(e) => setForm((f) => ({ ...f, invoice_number: e.target.value }))}
              className="arbi-input px-3 py-1.5 text-xs w-full"
              placeholder="INV-001"
            />
          </div>
          <div>
            <label htmlFor="inv-dist" className="text-xs font-medium text-ink-secondary block mb-1">
              Distributor
            </label>
            <div id="inv-dist">
              <ArbiSelect
                value={form.distributor_id}
                options={distributors.map((d) => ({ value: d.id, label: d.name }))}
                onChange={(v) => setForm((f) => ({ ...f, distributor_id: String(v), po_id: '' }))}
                placeholder="Select distributor..."
              />
            </div>
          </div>
          <div>
            <label htmlFor="inv-po" className="text-xs font-medium text-ink-secondary block mb-1">
              Linked PO
            </label>
            <div id="inv-po">
              <ArbiSelect
                value={form.po_id}
                options={poOptions}
                onChange={(v) => setForm((f) => ({ ...f, po_id: String(v) }))}
                placeholder="Select PO..."
                disabled={!form.distributor_id}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="inv-amount"
                className="text-xs font-medium text-ink-secondary block mb-1"
              >
                Amount
              </label>
              <input
                id="inv-amount"
                value={form.amount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, amount: e.target.value.replace(/[^0-9.]/g, '') }))
                }
                className="arbi-input px-3 py-1.5 text-xs w-full font-mono"
                placeholder="0.00"
                type="text"
                inputMode="decimal"
              />
            </div>
            <div>
              <label
                htmlFor="inv-status"
                className="text-xs font-medium text-ink-secondary block mb-1"
              >
                Status
              </label>
              <div id="inv-status">
                <ArbiSelect
                  value={form.status}
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'matched', label: 'Matched' },
                    { value: 'discrepancy', label: 'Discrepancy' },
                    { value: 'paid', label: 'Paid' },
                  ]}
                  onChange={(v) => setForm((f) => ({ ...f, status: String(v) as InvoiceStatus }))}
                />
              </div>
            </div>
          </div>
          <div>
            <label htmlFor="inv-due" className="text-xs font-medium text-ink-secondary block mb-1">
              Due Date
            </label>
            <input
              id="inv-due"
              value={form.due_date}
              onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
              className="arbi-input px-3 py-1.5 text-xs w-full font-mono"
              type="date"
            />
          </div>
          <div>
            <label
              htmlFor="inv-notes"
              className="text-xs font-medium text-ink-secondary block mb-1"
            >
              Notes
            </label>
            <textarea
              id="inv-notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="arbi-input px-3 py-1.5 text-xs w-full h-16 resize-none"
              placeholder="Optional notes..."
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <button onClick={onClose} className="arbi-btn-ghost text-xs px-3 py-1.5 cursor-pointer">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.invoice_number.trim()}
            className="arbi-btn-primary text-xs px-3 py-1.5 cursor-pointer disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Add Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Panel ────────────────────────────────────────────────────────────

interface InvoiceDetailPanelProps {
  invoice: InvoiceRow | null;
  onClose: () => void;
  onUpdate: () => void;
}

type GenericSupabaseRpc = (
  name: string,
  args: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

function InvoiceDetailPanel({
  invoice,
  onClose,
  onUpdate,
}: InvoiceDetailPanelProps): JSX.Element | null {
  const navigate = useNavigate();
  const [actionError, setActionError] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [linkOpts, setLinkOpts] = useState<{ value: string; label: string }[]>([]);
  if (!invoice) return null;

  const isOverdue = Boolean(
    invoice.status === 'pending' && invoice.due_date && new Date(invoice.due_date) < new Date()
  );

  const handleMarkPaid = async (): Promise<void> => {
    setActionError(null);
    // Guarded transition (Cluster 4): a stale mark-paid (invoice changed since load)
    // is REJECTED with a typed INVOICE.STALE_CONFLICT instead of silently overwriting.
    const env = extractEnvelope<{ status: string }>(
      await callRpc('transition_invoice_status', {
        p_invoice_id: invoice.id,
        p_expected_status: invoice.status,
        p_new_status: 'paid',
      })
    );
    if (!env.isOk) {
      setActionError(env.userErrors[0]?.message ?? 'Could not mark paid — please reload.');
      onUpdate();
      return;
    }
    onUpdate();
  };

  const handleLinkPo = async (): Promise<void> => {
    if (!invoice.distributor_id) return;
    const { data } = await supabase
      .from('purchase_orders')
      .select('id, po_number')
      .eq('distributor_id', invoice.distributor_id)
      .order('created_at', { ascending: false });
    setLinkOpts(
      ((data ?? []) as { id: string; po_number: string | null }[]).map((p) => ({
        value: p.id,
        label: p.po_number ?? '',
      }))
    );
    setLinking(true);
  };

  const handleSelectPo = async (poId: string): Promise<void> => {
    if (!poId) return;
    const { error } = await supabase
      .from('invoices')
      .update({ po_id: poId } as never)
      .eq('id', invoice.id);
    if (!error) {
      setLinking(false);
      onUpdate();
    }
  };

  return (
    <div className="w-80 border-l border-border bg-surface-1 flex flex-col flex-shrink-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <span className="text-sm font-semibold text-ink-bright">Invoice Detail</span>
        <button
          onClick={onClose}
          className="arbi-btn-ghost p-1 rounded cursor-pointer"
          aria-label="Close detail"
        >
          <X size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <p className="text-2xs text-ink-muted uppercase tracking-wider">Invoice #</p>
          <p className="text-sm font-mono text-ink-bright">{invoice.invoice_number}</p>
        </div>
        <div>
          <p className="text-2xs text-ink-muted uppercase tracking-wider">Status</p>
          <span
            className={clsx(
              'inline-block px-1.5 py-0.5 rounded text-2xs font-medium mt-1',
              STATUS_COLORS[invoice.status]
            )}
          >
            {invoice.status}
          </span>
        </div>
        {invoice.distributors?.name && (
          <div>
            <p className="text-2xs text-ink-muted uppercase tracking-wider">Distributor</p>
            <p className="text-xs text-ink-primary">{invoice.distributors.name}</p>
          </div>
        )}
        {invoice.purchase_orders?.po_number && (
          <div>
            <p className="text-2xs text-ink-muted uppercase tracking-wider">PO Number</p>
            <button
              onClick={() => navigate(`/purchasing/purchase-orders/${invoice.po_id}`)}
              className="text-xs font-mono text-accent hover:text-accent-hover cursor-pointer"
            >
              {invoice.purchase_orders.po_number}
            </button>
          </div>
        )}
        <div>
          <p className="text-2xs text-ink-muted uppercase tracking-wider">Amount</p>
          <p className="text-sm font-mono text-ink-bright">{fmtCurrency(invoice.amount)}</p>
        </div>
        {invoice.due_date && (
          <div>
            <p className="text-2xs text-ink-muted uppercase tracking-wider">Due Date</p>
            <p
              className={clsx('text-xs font-mono', isOverdue ? 'text-danger' : 'text-ink-primary')}
            >
              {new Date(invoice.due_date).toLocaleDateString()}
              {isOverdue && ' (overdue)'}
            </p>
          </div>
        )}
        {invoice.paid_at && (
          <div>
            <p className="text-2xs text-ink-muted uppercase tracking-wider">Paid At</p>
            <p className="text-xs font-mono text-ink-primary">{relativeTime(invoice.paid_at)}</p>
          </div>
        )}
        {invoice.notes && (
          <div>
            <p className="text-2xs text-ink-muted uppercase tracking-wider">Notes</p>
            <p className="text-xs text-ink-secondary">{invoice.notes}</p>
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border space-y-2">
        {actionError && (
          <p className="text-xs text-danger" role="alert">
            {actionError}
          </p>
        )}
        {invoice.status !== 'paid' && (
          <button
            onClick={handleMarkPaid}
            className="arbi-btn-primary text-xs w-full py-1.5 cursor-pointer"
          >
            Mark Paid
          </button>
        )}
        {!invoice.po_id && !linking && (
          <button
            onClick={handleLinkPo}
            className="arbi-btn-ghost text-xs w-full py-1.5 cursor-pointer"
          >
            Link to PO
          </button>
        )}
        {!invoice.po_id && linking && (
          <ArbiSelect
            value=""
            options={linkOpts}
            onChange={(v) => handleSelectPo(String(v))}
            placeholder="Select a PO to link..."
          />
        )}
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function FinanceInvoices(): JSX.Element {
  const { distributors } = useApp();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<InvoiceRow> | null>(null);
  const gridApiRef = useRef<GridApi<InvoiceRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatusFilter>('all');
  const [distFilter, setDistFilter] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceRow | null>(null);

  const stateRef = useRef<{
    searchText: string;
    statusFilter: InvoiceStatusFilter;
    distFilter: string;
  }>({ searchText, statusFilter, distFilter });
  stateRef.current = { searchText, statusFilter, distFilter };

  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, sortModel, successCallback, failCallback } = params;
    const { searchText: st, statusFilter: sf, distFilter: df } = stateRef.current;
    try {
      let query = supabase
        .from('invoices')
        .select('*, distributors(name), purchase_orders(po_number)', { count: 'exact' });

      if (sf !== 'all') query = query.eq('status', sf);
      if (df) query = query.eq('distributor_id', df);
      if (st.trim()) {
        // Day 76 P5.1 Phase 9A — escapeOrIlikeTerm adoption (Day 73 P4.1)
        const term = escapeOrIlikeTerm(st.trim());
        query = query.or(`invoice_number.ilike.${term},notes.ilike.${term}`);
      }

      if (sortModel.length > 0) {
        const s = sortModel[0];
        query = query.order(s.colId, { ascending: s.sort === 'asc', nullsFirst: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.range(startRow, endRow - 1);
      const { data, count, error } = await query;
      if (error) throw error;
      if (startRow === 0) setTotalCount(count || 0);
      successCallback((data as unknown as InvoiceRow[]) || [], count || 0);
    } catch (e) {
      reportDatasourceFailure(
        'Finance — Invoices',
        { rpcName: 'invoices', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<InvoiceRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
  }, [searchText, statusFilter, distFilter, getRowsFn]);

  const refreshGrid = useCallback((): void => {
    if (gridApiRef.current) gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
  }, [getRowsFn]);

  const columnDefs = useMemo<ColDef<InvoiceRow>[]>(
    () =>
      [
        {
          headerName: 'Invoice #',
          field: 'invoice_number',
          width: 140,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 500,
            color: 'var(--ink-bright)',
          },
        },
        {
          headerName: 'Distributor',
          valueGetter: (p: ValueGetterParams<InvoiceRow>) => p.data?.distributors?.name ?? null,
          width: 130,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'PO Number',
          field: 'po_id',
          width: 130,
          cellRenderer: (p: ICellRendererParams<InvoiceRow>) => {
            const poNum = p.data?.purchase_orders?.po_number;
            const poId = p.data?.po_id;
            if (!poNum) return <span className="text-ink-muted">{'\u2014'}</span>;
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (poId) navigate(`/purchasing/purchase-orders/${poId}`);
                }}
                className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
              >
                {poNum}
              </button>
            );
          },
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
          },
        },
        {
          headerName: 'Amount',
          field: 'amount',
          width: 120,
          valueFormatter: (p: ValueFormatterParams<InvoiceRow>) =>
            fmtCurrency(p.value as number | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 110,
          cellRenderer: (p: ICellRendererParams<InvoiceRow>) => {
            const status = p.data?.status;
            return p.data && status ? (
              <span
                className={clsx(
                  'px-1.5 py-0.5 rounded text-2xs font-medium',
                  STATUS_COLORS[status]
                )}
              >
                {status}
              </span>
            ) : null;
          },
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Due Date',
          field: 'due_date',
          width: 120,
          valueFormatter: (p: ValueFormatterParams<InvoiceRow>) =>
            p.value ? new Date(p.value as string).toLocaleDateString() : '\u2014',
          cellStyle: (p) => {
            const isOverdue = Boolean(
              p.data?.status === 'pending' &&
              p.data?.due_date &&
              new Date(p.data.due_date) < new Date()
            );
            return {
              display: 'flex',
              alignItems: 'center',
              fontFamily: "'JetBrains Mono', monospace",
              color: isOverdue ? 'var(--danger)' : 'var(--ink-primary)',
            };
          },
        },
        {
          headerName: 'Paid At',
          field: 'paid_at',
          width: 120,
          valueFormatter: (p: ValueFormatterParams<InvoiceRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
        {
          headerName: 'Notes',
          field: 'notes',
          flex: 1,
          minWidth: 100,
          cellStyle: { display: 'flex', alignItems: 'center', color: 'var(--ink-muted)' },
        },
      ] as ColDef<InvoiceRow>[],
    [navigate]
  );

  const defaultColDef = useMemo<ColDef<InvoiceRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-0)',
        headerBackgroundColor: 'var(--surface-1)',
        headerTextColor: 'var(--ink-muted)',
        foregroundColor: 'var(--ink-primary)',
        borderColor: 'var(--border)',
        rowHoverColor: 'var(--surface-3)',
        headerFontSize: 10,
        fontSize: 12,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback((event: RowClickedEvent<InvoiceRow>): void => {
    if (!event.data) return;
    setSelectedInvoice(event.data);
  }, []);

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Invoices</h1>
            {totalCount > 0 && (
              <span className="text-xs text-ink-muted">{totalCount.toLocaleString()}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ExportButton gridApiRef={gridApiRef} fileName="arbi-invoices" />
            <button
              onClick={() => setModalOpen(true)}
              className="arbi-btn-primary text-xs px-3 py-1 flex items-center gap-1 cursor-pointer"
            >
              <Plus size={12} /> Add Invoice
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                aria-pressed={statusFilter === tab.key}
                className={clsx(
                  'px-2 py-1 rounded text-2xs font-medium whitespace-nowrap transition-colors cursor-pointer',
                  statusFilter === tab.key
                    ? 'bg-accent/15 text-accent'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <ArbiSelect
            value={distFilter}
            options={[
              { value: '', label: 'All Distributors' },
              ...distributors.map((d) => ({ value: d.id, label: d.name })),
            ]}
            onChange={(v) => setDistFilter(String(v))}
            placeholder="Distributor..."
            className="w-40"
          />
          <div className="relative w-60 ml-auto">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search invoices..."
              aria-label="Search invoices"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-full"
            />
          </div>
        </div>
      </div>

      {/* Grid + Detail Panel */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        <div className="flex-1 p-2" style={{ minHeight: 0 }}>
          <AgGridReact
            ref={gridRef}
            theme={gridTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="infinite"
            cacheBlockSize={100}
            maxBlocksInCache={50}
            selectionColumnDef={SELECTION_COLUMN_DEF}
            rowSelection={{
              mode: 'multiRow',
              enableClickSelection: false,
              checkboxes: true,
              headerCheckbox: false,
            }}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            animateRows={false}
            headerHeight={36}
            rowHeight={42}
            suppressCellFocus={true}
            suppressHorizontalScroll={true}
            noRowsOverlayComponent={() => (
              <div className="flex flex-col items-center justify-center h-full">
                <FileText size={48} className="text-ink-muted mb-3" />
                <p className="text-sm text-ink-muted">
                  {statusFilter !== 'all' || searchText
                    ? 'No invoices match your filters'
                    : 'No invoices yet'}
                </p>
              </div>
            )}
          />
        </div>
        {selectedInvoice && (
          <InvoiceDetailPanel
            invoice={selectedInvoice}
            onClose={() => setSelectedInvoice(null)}
            onUpdate={() => {
              setSelectedInvoice(null);
              refreshGrid();
            }}
          />
        )}
      </div>

      {/* Add Invoice Modal */}
      {modalOpen && (
        <AddInvoiceModal
          distributors={distributors}
          onClose={() => setModalOpen(false)}
          onSaved={refreshGrid}
        />
      )}
    </div>
  );
}
